import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * PageTitleContext
 *
 * Allows child components (like form views) to dynamically override
 * the page title displayed in the layout header.
 *
 * Usage in child components:
 * const { setPageTitle, clearPageTitle } = usePageTitle();
 * useEffect(() => {
 *   setPageTitle(requestName);
 *   return () => clearPageTitle();
 * }, [requestName]);
 */
const PageTitleContext = createContext({
  customTitle: null,
  setPageTitle: () => {},
  clearPageTitle: () => {},
});

export const PageTitleProvider = ({ children }) => {
  const [customTitle, setCustomTitle] = useState(null);

  const setPageTitle = useCallback((title) => {
    setCustomTitle(title);
  }, []);

  const clearPageTitle = useCallback(() => {
    setCustomTitle(null);
  }, []);

  return (
    <PageTitleContext.Provider
      value={{ customTitle, setPageTitle, clearPageTitle }}
    >
      {children}
    </PageTitleContext.Provider>
  );
};

export const usePageTitle = () => {
  const context = useContext(PageTitleContext);
  if (!context) {
    throw new Error('usePageTitle must be used within a PageTitleProvider');
  }
  return context;
};

export default PageTitleContext;
